(() => {
  const videos = new WeakMap();
  const carousels = new WeakMap();
  const localization = new WeakMap();
  const mediaObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(({target, isIntersecting}) => {
        if (!isIntersecting && !target.paused) target.pause();
      }), {threshold: 0.05})
    : null;
  const resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver(entries => entries.forEach(({target}) => {
        const controls = carousels.get(target)?.controls;
        if (controls) controls.hidden = target.scrollWidth <= target.clientWidth + 2;
      }))
    : null;

  const elements = (root, selector) => {
    const matches = Array.from(root.querySelectorAll(selector));
    if (root.matches?.(selector)) matches.unshift(root);
    return matches;
  };

  const init = (root = document) => {
    elements(root, '[data-spe-stylesheet]').forEach(section => {
      const href = new URL(section.dataset.speStylesheet, document.baseURI).href;
      if (!Array.from(document.querySelectorAll('link[rel=stylesheet]')).some(link => link.href === href)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.append(link);
      }
    });
    elements(root, '[data-spe-section] video').forEach(video => {
      if (videos.has(video)) return;
      const controller = new AbortController();
      const options = {signal: controller.signal};
      videos.set(video, controller);
      video.preload = 'none';
      video.controls = true;
      video.removeAttribute('autoplay');
      const player = video.closest('internal-video');
      if (player) {
        player.querySelector('.internal-video__play')?.addEventListener('click', () => video.play().catch(() => {}), options);
        video.addEventListener('play', () => { player.dataset.playing = 'true'; }, options);
        const paused = () => { player.dataset.playing = 'false'; };
        video.addEventListener('pause', paused, options);
        video.addEventListener('ended', paused, options);
        video.addEventListener('timeupdate', () => {
          const percent = Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration * 100 : 0;
          player.style.setProperty('--completed', percent + '%');
        }, options);
      }
      mediaObserver?.observe(video);
    });
    elements(root, '[data-spe-section] splide-component').forEach(component => {
      const track = component.querySelector('.splide__track');
      if (!track || carousels.has(track)) return;
      const controller = new AbortController();
      const controls = component.querySelector('.spe-carousel-controls') || document.createElement('div');
      controls.className = 'spe-carousel-controls';
      controls.replaceChildren();
      for (const [direction, label, icon] of [[-1, 'Previous', '‹'], [1, 'Next', '›']]) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', label);
        button.textContent = icon;
        button.addEventListener('click', () => {
          const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
          const target = direction > 0 && track.scrollLeft >= maximum - 2 ? 0
            : direction < 0 && track.scrollLeft <= 2 ? maximum
            : Math.max(0, Math.min(maximum, track.scrollLeft + direction * track.clientWidth * 0.8));
          track.scrollTo({left: target, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
        }, {signal: controller.signal});
        controls.append(button);
      }
      component.append(controls);
      carousels.set(track, {controller, controls});
      resizeObserver?.observe(track);
      controls.hidden = track.scrollWidth <= track.clientWidth + 2;
    });
    elements(root, '[data-spe-section] .stepprs-native-localization select').forEach(select => {
      if (localization.has(select)) return;
      const controller = new AbortController();
      localization.set(select, controller);
      select.addEventListener('change', () => select.form?.requestSubmit(), {signal: controller.signal});
    });
  };

  const unload = root => {
    elements(root, 'video').forEach(video => {
      mediaObserver?.unobserve(video);
      videos.get(video)?.abort();
      videos.delete(video);
      if (!video.paused) video.pause();
    });
    elements(root, '.splide__track').forEach(track => {
      resizeObserver?.unobserve(track);
      carousels.get(track)?.controller.abort();
      carousels.delete(track);
    });
    elements(root, '.stepprs-native-localization select').forEach(select => {
      localization.get(select)?.abort();
      localization.delete(select);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once: true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  const clearReveal = () => document.querySelectorAll('.spe-editor-reveal').forEach(element => element.classList.remove('spe-editor-reveal'));
  document.addEventListener('shopify:block:select', event => {
    clearReveal();
    for (let element = event.target; element && element !== document.body; element = element.parentElement) {
      if (element.matches('.mobile-hidden,.desktop-hidden')) element.classList.add('spe-editor-reveal');
    }
  });
  document.addEventListener('shopify:block:deselect', clearReveal);
})();
